import React, { useState, useEffect, useRef, useCallback } from 'react';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Chat({ threads, setThreads, activeThread, setActiveThread, models, fetchThreads }) {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [editingName, setEditingName] = useState(null);
  const [newName, setNewName] = useState('');
  const [attachedFile, setAttachedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [activeThread?.messages, scrollToBottom]);

  const createThread = async () => {
    try {
      const res = await fetch(`${API_URL}/api/threads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: `Thread ${threads.length + 1}` })
      });
      const thread = await res.json();
      setThreads([...threads, thread]);
      setActiveThread(thread);
    } catch (err) {
      logger.error('Failed to create thread:', err);
    }
  };

  const deleteThread = async (id) => {
    if (!window.confirm('Delete this thread?')) return;
    try {
      await fetch(`${API_URL}/api/threads/${id}`, { method: 'DELETE' });
      const newThreads = threads.filter(t => t.id !== id);
      setThreads(newThreads);
      if (activeThread?.id === id) {
        setActiveThread(newThreads[0] || null);
      }
    } catch (err) {
      logger.error('Failed to delete thread:', err);
    }
  };

  const selectThread = async (thread) => {
    try {
      const res = await fetch(`${API_URL}/api/threads/${thread.id}`);
      const data = await res.json();
      setActiveThread(data);
    } catch (err) {
      logger.error('Failed to load thread:', err);
    }
  };

  const sendMessage = async () => {
    if ((!input.trim() && !attachedFile) || !activeThread || loading) return;

    const userMessage = input.trim();
    const threadId = activeThread.id;
    const fileData = attachedFile;
    
    setInput('');
    setAttachedFile(null);
    setLoading(true);

    let displayMessage = userMessage;
    if (fileData) {
      displayMessage = `📎 ${fileData.filename}\n\n${userMessage}`;
    }

    setActiveThread(prev => ({
      ...prev,
      messages: [...prev.messages, { role: 'user', content: displayMessage }]
    }));

    try {
      const res = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          thread_id: threadId, 
          message: userMessage,
          file_content: fileData?.content || null,
          file_name: fileData?.filename || null
        })
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || 'Chat request failed');
      }

      const data = await res.json();
      
      setActiveThread(prev => ({
        ...prev,
        messages: [...prev.messages, { role: 'assistant', content: data.response }]
      }));
    } catch (err) {
      logger.error('Chat error:', err);
      alert(`Error: ${err.message}`);
      setActiveThread(prev => ({
        ...prev,
        messages: prev.messages.slice(0, -1)
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        body: formData
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || 'Upload failed');
      }

      const data = await res.json();
      setAttachedFile(data);
      
      if (data.truncated) {
        alert('File was truncated to 50,000 characters due to size limits.');
      }
    } catch (err) {
      logger.error('Upload error:', err);
      alert(`Failed to upload: ${err.message}`);
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeAttachedFile = () => {
    setAttachedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const updateModel = async (provider, model) => {
    if (!activeThread) return;
    try {
      await fetch(`${API_URL}/api/threads/${activeThread.id}/model`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider, model, display_name: '' })
      });
      setActiveThread({ ...activeThread, model, provider });
      setShowSettings(false);
    } catch (err) {
      logger.error('Failed to update model:', err);
    }
  };

  const updateThreadName = async (id, name) => {
    try {
      await fetch(`${API_URL}/api/threads/${id}/name`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });
      setThreads(threads.map(t => t.id === id ? { ...t, name } : t));
      if (activeThread?.id === id) {
        setActiveThread({ ...activeThread, name });
      }
      setEditingName(null);
    } catch (err) {
      logger.error('Failed to update name:', err);
    }
  };

  const exportThread = async () => {
    if (!activeThread) return;
    try {
      const res = await fetch(`${API_URL}/api/threads/${activeThread.id}/export`);
      const data = await res.json();
      
      const blob = new Blob([data.content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      logger.error('Failed to export:', err);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="chat-view" data-testid="chat-view">
      {/* Thread Sidebar */}
      <div className="thread-sidebar">
        <button className="new-thread-btn" onClick={createThread} data-testid="new-thread-btn">
          <span>+</span> New Thread
        </button>
        <div className="threads-list" data-testid="threads-list">
          {threads.map(thread => (
            <div 
              key={thread.id}
              className={`thread-item ${activeThread?.id === thread.id ? 'active' : ''}`}
              data-testid={`thread-item-${thread.id}`}
            >
              {editingName === thread.id ? (
                <input
                  className="thread-name-input"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onBlur={() => updateThreadName(thread.id, newName)}
                  onKeyDown={(e) => e.key === 'Enter' && updateThreadName(thread.id, newName)}
                  autoFocus
                />
              ) : (
                <>
                  <span 
                    className="thread-name" 
                    onClick={() => selectThread(thread)}
                    onDoubleClick={() => {
                      setEditingName(thread.id);
                      setNewName(thread.name);
                    }}
                  >
                    {thread.name}
                  </span>
                  <button 
                    className="delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteThread(thread.id);
                    }}
                  >
                    ×
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Chat Area */}
      <div className="chat-area">
        {/* Chat Header */}
        <div className="chat-header">
          <div className="header-center">
            {activeThread && (
              <span className="current-model">
                {models.find(m => m.model === activeThread.model)?.display_name || activeThread.model}
              </span>
            )}
          </div>
          <div className="header-actions">
            <button 
              className="action-btn"
              onClick={() => setShowSettings(!showSettings)}
              title="Settings"
              data-testid="settings-btn"
            >
              ⚙️
            </button>
            <button 
              className="action-btn"
              onClick={exportThread}
              title="Export"
              disabled={!activeThread}
              data-testid="export-btn"
            >
              📥
            </button>
          </div>
        </div>

        {/* Settings Modal */}
        {showSettings && (
          <div className="settings-modal" data-testid="settings-modal">
            <div className="settings-content">
              <h3>Select Model</h3>
              <div className="models-grid">
                {models.map(m => (
                  <button
                    key={`${m.provider}-${m.model}`}
                    className={`model-btn ${activeThread?.model === m.model ? 'active' : ''}`}
                    onClick={() => updateModel(m.provider, m.model)}
                  >
                    <span className="model-name">{m.display_name}</span>
                    <span className="model-provider">{m.provider}</span>
                  </button>
                ))}
              </div>
              <button className="close-settings" onClick={() => setShowSettings(false)}>
                Close
              </button>
            </div>
          </div>
        )}

        {/* Messages */}
        <div className="messages" data-testid="messages-container">
          {!activeThread ? (
            <div className="no-thread" data-testid="no-thread-message">
              <div className="no-thread-icon">🦋</div>
              <h2>Welcome to Metamatrix</h2>
              <p>Create a new thread to begin your correspondence</p>
              <button className="start-btn" onClick={createThread} data-testid="start-btn">
                Start Correspondence
              </button>
            </div>
          ) : (
            <>
              {activeThread.messages.map((msg, i) => (
                <div key={`msg-${activeThread.id}-${i}`} className={`message ${msg.role}`}>
                  <div className="message-role">
                    {msg.role === 'user' ? 'You' : 'Claude'}
                  </div>
                  <div className="message-content">{msg.content}</div>
                </div>
              ))}
              {loading && (
                <div className="message assistant loading">
                  <div className="message-role">Claude</div>
                  <div className="message-content">
                    <span className="typing-indicator">
                      <span></span><span></span><span></span>
                    </span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input */}
        {activeThread && (
          <div className="input-area" data-testid="input-area">
            {attachedFile && (
              <div className="attached-file">
                <span className="file-icon">📎</span>
                <span className="file-name">{attachedFile.filename}</span>
                <button className="remove-file-btn" onClick={removeAttachedFile}>×</button>
              </div>
            )}
            <div className="input-container">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".pdf,.txt,.md,.json,.py,.js,.html,.css"
                style={{ display: 'none' }}
              />
              <button 
                className="attach-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={loading || uploadingFile}
                title="Attach file"
              >
                {uploadingFile ? '...' : '📎'}
              </button>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your message..."
                disabled={loading}
                rows={1}
                data-testid="message-input"
              />
              <button 
                className="send-btn"
                onClick={sendMessage}
                disabled={loading || (!input.trim() && !attachedFile)}
                data-testid="send-btn"
              >
                {loading ? '...' : 'Send'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Chat;
